// Typisierter API-Client der blackfortflow-Editor-SPA.
//
// Das Frontend spricht ausschließlich das editor-backend (same-origin, Bearer-Token folgt in
// einer späteren Auth-Stufe). Alle Pfade sind zu docs/api.md konsistent (/api/v1, /ws/v1).
// Jeder Fehler wird zu einer ApiError verdichtet, die die Statusleiste/Toasts anzeigen können.

import type {
  AppConfig,
  LlmConfigUpdate,
  NotebookGenerateResult,
  NotebookRunResult,
  PodOptionsResponse,
  ProjectLlmConfig,
  TrainingGoalEntry,
  TrainingGoalsConfig,
  TrainingPodConfig,
  TrainingRunSummary,
  TrainingPodUpdate,
  BlocksResponse,
  BorisImportResult,
  SuperblockImportResult,
  SuperblockGapReport,
  ModbusMapDraftResult,
  ModbusMapProposal,
  ModbusMapDecision,
  OnboardingAuthorization,
  DeployApprove,
  DeployCreate,
  DeployInfo,
  ProjectSettingsUpdate,
  DataFileColumns,
  DataFileInfo,
  DataFileList,
  DataSources,
  ModelList,
  ModelManifest,
  PlantSchematic,
  ProjectCreate,
  ProjectFourEyesResult,
  ProjectList,
  ProjectMeta,
  PaceRequest,
  RunCreate,
  RunInfo,
  RunList,
  SignalCatalog,
  SignalGraph,
  UaBrowseDraftResult,
  UaBrowseDecision,
  UaBrowseAuthorization,
  SuperblockSummary,
  StampRequest,
  StampResult,
  SymbolsResponse,
  ValidateResult,
  WriteResult,
  ApiErrorBody,
  MonitorMessage,
  UserInfo,
  HistoryResponse,
  HistoryDiff,
  AuditResponse,
  LoginRequest,
  LoginResponse,
  MfaLoginRequest,
  MfaEnrollResponse,
  MfaActivateResponse,
  MfaStatusResponse,
  TokenResponse,
  UserCreate,
  UserList,
  UserUpdate,
  MonitoringConfig,
  RegisterRequest,
  RegisterResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  RegistrationQueue,
  ApproveRequest,
  RejectRequest,
  RegistrationDecisionResponse,
} from './types';

// Rollen, die im Backend das Recht „editieren" tragen (RIGHT_ROLES in auth.py). Wird hier
// gespiegelt, damit das Frontend den Revert-Knopf nur denen zeigt, die ihn auch ausführen
// dürfen — die harte Durchsetzung bleibt beim Backend (403 bei fehlendem Recht).
const EDIT_ROLES = ['betreiber', 'data_scientist', 'admin'] as const;

// Leitet aus den Rollen eines Principals das Recht „editieren" ab (Spiegel von principal.has_right).
export function hasEditRight(roles: readonly string[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => (EDIT_ROLES as readonly string[]).includes(r));
}

// Rollen, die im Backend das Recht „deployen" tragen (RIGHT_ROLES['deployen'] in auth.py).
// Gespiegelt, damit die Registry-Ansicht die Governance-Aktionen (Freigabe/Aktivieren) nur denen
// zeigt, die sie ausführen dürfen — die harte Durchsetzung bleibt beim Backend (403).
const DEPLOY_ROLES = ['deployer', 'admin'] as const;

// Leitet aus den Rollen eines Principals das Recht „deployen" ab (Spiegel von principal.has_right).
export function hasDeployRight(roles: readonly string[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => (DEPLOY_ROLES as readonly string[]).includes(r));
}

import { clearToken, getToken } from '../auth/token';

const API = '/api/v1';
const WS = '/ws/v1';

// Ein sprechender Fehler: trägt Backend-Code und -Meldung, damit die UI konkret melden kann,
// was schiefging (nicht nur „HTTP 422").
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Extrahiert den einheitlichen Fehlerkörper des Backends; fällt fail-safe auf den Status-Text
// zurück, falls die Antwort keinen erwartbaren JSON-Körper trägt.
async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as Partial<ApiErrorBody>;
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    /* kein JSON-Körper — Status-Text genügt */
  }
  return new ApiError(res.status, code, message);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  const token = getToken();
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        // Bearer-Token aus der lokalen Anmeldung (falls vorhanden) an jeden Request hängen.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
      credentials: 'same-origin',
    });
  } catch (cause) {
    // Netzfehler (Backend nicht erreichbar) sauber in eine ApiError überführen.
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause));
  }
  // 401 ⇒ Token fehlt/ungültig: verwerfen und die Login-Schicht anstoßen.
  if (res.status === 401) {
    clearToken();
    throw await toApiError(res);
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Frontend-Konfiguration ────────────────────────────────────────────────────────────

// Die vom Backend gelieferte Frontend-Konfiguration lesen (u. a. ob die Trainings-Ansicht mit
// eingebettetem JupyterLab angeboten wird und unter welchem Basispfad). Die Trainings-Ansicht liest
// das beim Betreten, um das iframe nur bei aktivierter Umgebung einzubetten.
export function getConfig(): Promise<AppConfig> {
  return request<AppConfig>(`${API}/config`);
}

// ── Trainings-/GPU-Pod-Konfiguration (Issue #222) ────────────────────────────────────

// Die volle Pod-Konfiguration des ANGEMELDETEN Nutzers lesen (Provider/Konto/GPU/Region/Limits +
// secretRefs als OpenBao-Handles). Seit Issue #314 gehört sie dem Nutzer, nicht der Instanz.
// `user` setzt die Kennung eines anderen Nutzers — das darf nur ein Admin (sonst 403).
// Erlaubte Pod-Werte je Provider (#368). EINMAL beim Öffnen des Editors laden und lokal filtern —
// die Tabelle ändert sich zwischen zwei Klicks nicht.
export function getTrainingPodOptions(): Promise<PodOptionsResponse> {
  return request<PodOptionsResponse>(`${API}/training/pod/options`);
}

export function getTrainingPod(user?: string): Promise<TrainingPodConfig> {
  const query = user ? `?user=${encodeURIComponent(user)}` : '';
  return request<TrainingPodConfig>(`${API}/training/pod${query}`);
}

// Die eigene Pod-Konfiguration setzen (partielles Update). Seit Issue #314 braucht das kein
// Admin-Recht mehr — jeder richtet seinen eigenen Pod ein; `user` (nur Admin) setzt eine fremde.
// Klartext-Geheimnisse in secretRefs weist das Backend fail-closed ab — hier gehören nur
// OpenBao-Handles hinein.
export function setTrainingPod(patch: TrainingPodUpdate, user?: string): Promise<TrainingPodConfig> {
  const query = user ? `?user=${encodeURIComponent(user)}` : '';
  return request<TrainingPodConfig>(`${API}/training/pod${query}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// ── Trainings-Zielparameter (Issue #355) ──────────────────────────────────────────────
// Die gewählten Optimierungsziele eines Projekts samt Begründung lesen. Die Antwort enthält
// zusätzlich `available`: die am mpc-optimizer-Knoten des Graphen definierten Ziele. Die kommen
// abgeleitet aus dem Graphen und werden nicht gespeichert — es gibt genau eine Wahrheitsquelle.
export function getTrainingGoals(projectId: string): Promise<TrainingGoalsConfig> {
  return request<TrainingGoalsConfig>(`${API}/projects/${encodeURIComponent(projectId)}/training-goals`);
}

// Auswahl + Begründung schreiben (git-Commit + Audit im Backend). `available` wird bewusst NICHT
// mitgeschickt: das Backend verwirft es ohnehin, und ein Client soll die Zielliste nicht
// einfrieren können. Ein gewähltes Ziel, das der Graph nicht kennt, wird mit 422 abgewiesen.
export function setTrainingGoals(
  projectId: string,
  entries: TrainingGoalEntry[],
): Promise<{ rev: string }> {
  return request<{ rev: string }>(`${API}/projects/${encodeURIComponent(projectId)}/training-goals`, {
    method: 'PUT',
    body: JSON.stringify({ schema_version: 1, entries }),
  });
}

// Einen Trainingslauf auf dem eigenen GPU-Pod starten. Die gewählten Zielparameter gehen als
// Lauf-Parameter mit; im Pod kommen sie als BFF_PARAM_<NAME> an.
export function startTrainingRun(payload: {
  notebook: string;
  modelId?: string;
  version?: string;
  parameters?: Record<string, unknown>;
}): Promise<TrainingRunSummary> {
  return request<TrainingRunSummary>(`${API}/training/pod/runs`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── LLM-Zugang je Projekt (Issue #359) ────────────────────────────────────────────────
// Den Zugang lesen: geheimnisfrei (nur ob/welche Form/letzte vier Zeichen) plus die effektive
// Auflösung über Projekt → Nutzer → Server.
export function getProjectLlmConfig(projectId: string): Promise<ProjectLlmConfig> {
  return request<ProjectLlmConfig>(`${API}/projects/${encodeURIComponent(projectId)}/llm-config`);
}

// Den Zugang setzen. Das Schlüsselfeld darf einen rohen Schlüssel ODER einen OpenBao-Handle
// tragen; ein roher Schlüssel wird SERVERSEITIG verschlüsselt. Die Antwort ist wieder die
// geheimnisfreie Sicht — der gesetzte Schlüssel kommt nie zurück.
export function setProjectLlmConfig(
  projectId: string,
  patch: LlmConfigUpdate,
): Promise<ProjectLlmConfig> {
  return request<ProjectLlmConfig>(`${API}/projects/${encodeURIComponent(projectId)}/llm-config`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// ── Notebook-Builder (Issue #363) ─────────────────────────────────────────────────────
// Erzeugt ein Trainings-Notebook aus dem Anlagenmodell und legt es als Snapshot ab. Es läuft
// dabei NICHT — zwischen „ein Modell hat Code geschrieben" und „er läuft auf der Appliance"
// gehört ein Mensch.
export function generateNotebook(
  projectId: string,
  payload: { notebook?: string; modelId?: string; version?: string; provider?: string },
): Promise<NotebookGenerateResult> {
  return request<NotebookGenerateResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/notebook-builder/generate`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// Führt einen zuvor erzeugten Snapshot aus und zieht den Graphen auf das Ergebnis.
export function runGeneratedNotebook(
  projectId: string,
  payload: { sha256: string; modelId?: string; version?: string; importIntoGraph?: boolean },
): Promise<NotebookRunResult> {
  return request<NotebookRunResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/notebook-builder/run`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// ── Anmeldung & eigener Principal (Issue #82/#86) ──────────────────────────────────────
// Passwort-Login gegen den User-Store: liefert ein frisches Bearer-Token (einmalig im Klartext)
// plus den Principal. Der Aufrufer (LoginGate) speichert das Token über setToken; der request()-
// Pfad hängt es danach an jeden Aufruf. Kein Token wird hier automatisch gesetzt — bewusst, damit
// die Login-Schicht die Persistenz kontrolliert.
export function login(username: string, password: string): Promise<LoginResponse> {
  const payload: LoginRequest = { username, password };
  return request<LoginResponse>(`${API}/auth/login`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Zweiter Login-Schritt bei gekoppeltem TOTP (Issue #192): Ticket aus Schritt 1 plus
// Authenticator-Code ODER Recovery-Code. Erst hier kommt (bei Erfolg) das Bearer-Token.
export function loginMfa(payload: MfaLoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>(`${API}/auth/login/mfa`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── TOTP-MFA-Kopplung des eigenen Kontos (Issue #192) ──────────────────────────────────

// MFA-Status des eigenen Kontos (ob gekoppelt, wie viele Recovery-Codes noch offen sind).
export function getMyMfa(): Promise<MfaStatusResponse> {
  return request<MfaStatusResponse>(`${API}/me/mfa`);
}

// Kopplung starten: liefert Secret + otpauth-URI (das Frontend rendert daraus den QR). Noch nicht
// aktiv — erst activateMfa mit dem ersten Code schaltet scharf.
export function enrollMfa(): Promise<MfaEnrollResponse> {
  return request<MfaEnrollResponse>(`${API}/me/mfa/enroll`, { method: 'POST' });
}

// Kopplung bestätigen mit dem ersten Authenticator-Code → MFA aktiv + Recovery-Codes (einmalig).
export function activateMfa(code: string): Promise<MfaActivateResponse> {
  return request<MfaActivateResponse>(`${API}/me/mfa/activate`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// MFA wieder abschalten (mit dem aktuellen Passwort zur Bestätigung). Liefert den Nutzer zurück.
export function disableMfa(currentPassword: string): Promise<UserInfo> {
  return request<UserInfo>(`${API}/me/mfa/disable`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword }),
  });
}

// Den authentisierten Nutzer samt Rollen lesen (GET /me). Das Frontend leitet daraus per
// hasEditRight ab, ob es rechte-gebundene Aktionen (z. B. Revert) anbietet, und ob der
// Settings-Tab (Benutzer-Verwaltung) sichtbar ist.
export function getMe(): Promise<UserInfo> {
  return request<UserInfo>(`${API}/me`);
}

// Serverseitiger Logout (Issue #197): widerruft das eigene Token im Store (der zuvor kopierte
// Wert löst danach nicht mehr auf). Antwortet mit 204. Der Aufrufer verwirft danach den Token
// lokal (clearToken) und zeigt den Anmelde-Screen. Bewusst tolerant: ist der Aufruf nicht möglich
// (kein/statisches Token), reicht der lokale Logout — der Client fängt das ab.
export function logout(): Promise<void> {
  return request<void>(`${API}/auth/logout`, { method: 'POST' });
}

// ── Selbstregistrierung + E-Mail-Verifikation (Issue #186) ─────────────────────────────
// Diese drei Endpunkte sind nur aktiv, wenn das Backend BFF_ALLOW_SELF_REGISTRATION gesetzt hat
// (config.allowSelfRegistration). Ist das Feature aus, antworten sie fail-closed mit 404 — das
// Frontend blendet das Registrieren-Formular dann anhand des config-Flags gar nicht erst ein.

// Selbstregistrierung: legt ein Konto im Status pending_email an und stößt die Verifikationsmail
// an. Doppelregistrierung → 409 already_registered (ohne zu verraten, welches Feld kollidiert).
export function register(payload: RegisterRequest): Promise<RegisterResponse> {
  return request<RegisterResponse>(`${API}/auth/register`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// E-Mail bestätigen (Kennung + Token aus dem Mail-Link). Nutzt bewusst den POST-Weg mit Body —
// robuster als der reine GET-Link, weil beide Felder mitgehen. Ungültig/abgelaufen → 400 invalid_token.
export function verifyEmail(identity: string, token: string): Promise<VerifyEmailResponse> {
  const payload: VerifyEmailRequest = { identity, token };
  return request<VerifyEmailResponse>(`${API}/auth/verify-email`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Verifikationsmail erneut anfordern (rate-limited, neutrale Antwort, keine Enumeration).
export function resendVerification(identity: string): Promise<ResendVerificationResponse> {
  const payload: ResendVerificationRequest = { identity };
  return request<ResendVerificationResponse>(`${API}/auth/resend-verification`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── Passwort vergessen / zurücksetzen (Issue #189) ─────────────────────────────────────
// Beide antworten neutral gleich (keine E-Mail-/Kennungs-Enumeration). Nicht feature-geflaggt:
// auch ein Admin-angelegtes Konto mit E-Mail darf sein Passwort zurücksetzen.

// Passwort-Reset anfordern (E-Mail rein). Immer neutrale Bestätigung; Rate-Limit → 429.
export function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  const payload: ForgotPasswordRequest = { email };
  return request<ForgotPasswordResponse>(`${API}/auth/forgot-password`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Passwort mit Reset-Token neu setzen. Token einmalig; die bestehende Session wird serverseitig
// entwertet. Ungültig/abgelaufen/verbraucht → 400 invalid_token.
export function resetPassword(
  identity: string,
  token: string,
  newPassword: string,
): Promise<ResetPasswordResponse> {
  const payload: ResetPasswordRequest = { identity, token, newPassword };
  return request<ResetPasswordResponse>(`${API}/auth/reset-password`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── Admin-Freigabe-Warteschlange (Issue #187, nur Admin) ───────────────────────────────
// Das Backend erzwingt das admin-Recht (401 ohne Token, 403 ohne Admin-Rolle). approve/reject sind
// nur aus pending_approval zulässig (sonst 409 not_pending).

// Freigabe-Warteschlange lesen (Default status=pending_approval). Unbekannter Status → 422.
export function listRegistrations(status?: string): Promise<RegistrationQueue> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<RegistrationQueue>(`${API}/admin/registrations${q}`);
}

// Ein Konto freigeben (pending_approval → active), optional mit initialen Rollen.
export function approveRegistration(
  identity: string,
  roles?: string[],
): Promise<RegistrationDecisionResponse> {
  const payload: ApproveRequest = roles != null ? { roles } : {};
  return request<RegistrationDecisionResponse>(
    `${API}/admin/registrations/${encodeURIComponent(identity)}/approve`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// Ein Konto dauerhaft ablehnen (pending_approval → rejected), optional mit Grund.
export function rejectRegistration(
  identity: string,
  reason?: string,
): Promise<RegistrationDecisionResponse> {
  const payload: RejectRequest = reason ? { reason } : {};
  return request<RegistrationDecisionResponse>(
    `${API}/admin/registrations/${encodeURIComponent(identity)}/reject`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// Eigenes Passwort ändern (PUT /me/password) — altes zur Bestätigung, neues zum Setzen.
export function changeMyPassword(currentPassword: string, newPassword: string): Promise<UserInfo> {
  return request<UserInfo>(`${API}/me/password`, {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// ── Benutzer-/Rollenverwaltung (nur Admin, Issue #86) ──────────────────────────────────

export function listUsers(): Promise<UserList> {
  return request<UserList>(`${API}/users`);
}

export function createUser(payload: UserCreate): Promise<UserInfo> {
  return request<UserInfo>(`${API}/users`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateUser(identity: string, patch: UserUpdate): Promise<UserInfo> {
  return request<UserInfo>(`${API}/users/${encodeURIComponent(identity)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export function deleteUser(identity: string): Promise<void> {
  return request<void>(`${API}/users/${encodeURIComponent(identity)}`, {
    method: 'DELETE',
  });
}

// Passwort eines fremden Nutzers (neu) setzen (Admin).
export function setUserPassword(identity: string, password: string): Promise<UserInfo> {
  return request<UserInfo>(`${API}/users/${encodeURIComponent(identity)}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}

// Bearer-Token eines Nutzers rotieren: liefert das neue Token EINMALIG im Klartext.
export function rotateUserToken(identity: string): Promise<TokenResponse> {
  return request<TokenResponse>(`${API}/users/${encodeURIComponent(identity)}/rotate-token`, {
    method: 'POST',
  });
}

// ── Palette ─────────────────────────────────────────────────────────────────────────

export function getBlocks(): Promise<BlocksResponse> {
  return request<BlocksResponse>(`${API}/palette/blocks`);
}

export function getSymbols(domain?: string): Promise<SymbolsResponse> {
  const q = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  return request<SymbolsResponse>(`${API}/palette/symbols${q}`);
}

export function getSuperblocks(): Promise<{ superblocks: SuperblockSummary[] }> {
  return request<{ superblocks: SuperblockSummary[] }>(`${API}/palette/superblocks`);
}

// Innerer (read-only) Signalgraph eines Superblocks — für das Aufklappen im Editor.
export function getSuperblockGraph(ref: string): Promise<{ graph: SignalGraph }> {
  return request<{ graph: SignalGraph }>(
    `${API}/palette/superblocks/${encodeURIComponent(ref)}/graph`,
  );
}

// ── Superblock-Import (.SBL) (Issue #106) ───────────────────────────────────────────────
// boris2py-.SBL-Dateien in die instanzweite Superblock-Bibliothek importieren (multipart, Feld
// `sbl`, ein oder mehrere Dateien). Gültige landen in der Palette (wie die Defaults). Kein
// Content-Type setzen — der Browser setzt die multipart-Grenze selbst; deshalb NICHT über
// request() (das würde bei body JSON annehmen). Das Recht „veröffentlichen" erzwingt das Backend
// (403). Löschen entfernt eine importierte .SBL wieder; Defaults sind nicht löschbar (Backend-Fehler).
export async function importSuperblocks(
  files: File[],
  expected?: string[],
): Promise<SuperblockImportResult> {
  const form = new FormData();
  for (const file of files) form.append('sbl', file);
  // Für den Mehrfach-Durchlauf (#271): die vom Projekt/Bundle referenzierten Refs mitgeben, damit
  // das Backend in `stillMissing` zurückgibt, was NACH diesem Import (inkl. verschachtelter Refs)
  // noch fehlt.
  if (expected && expected.length) form.append('expected', expected.join('\n'));
  let res: Response;
  try {
    res = await fetch(`${API}/superblocks/import`, {
      method: 'POST',
      body: form,
      headers: {
        Accept: 'application/json',
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause));
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as SuperblockImportResult;
}

// Eine importierte .SBL aus der Bibliothek entfernen (Defaults nicht löschbar → Backend-Fehler).
export function deleteSuperblock(ref: string): Promise<void> {
  return request<void>(`${API}/superblocks/${encodeURIComponent(ref)}`, {
    method: 'DELETE',
  });
}

// ── Modbus-Register-Map-Import + Mapping (#156, C1.2) ────────────────────────────────────
// Eine Register-Map (CSV/Hersteller-Tabelle) hochladen → niedrig-konfidente Register→Tag-
// Vorschläge + Review-Overlay. multipart (Feld `file`); der Browser setzt die Grenze selbst, daher
// KEIN Content-Type-Header. Es wird kein Paket an die Anlage gesendet und kein Adressraum
// abgetastet (Import statt Sweep); nichts wird gespeichert.
export async function importModbusMap(
  projectId: string,
  file: File,
): Promise<ModbusMapDraftResult> {
  const form = new FormData();
  form.append('file', file);
  let res: Response;
  try {
    res = await fetch(`${API}/projects/${encodeURIComponent(projectId)}/modbus-map/import`, {
      method: 'POST',
      body: form,
      headers: {
        Accept: 'application/json',
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause));
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as ModbusMapDraftResult;
}

// Die Register-Map-Vorschläge mit den Mapping-Entscheidungen bestätigen und schreiben. Jeder
// Vorschlag braucht eine Entscheidung; nur bestätigte, bindbare Zeilen bleiben. Die geschriebenen
// Variablen tragen ihren modbus-Block (rein lesend, direction=input) — kein Schreibpfad.
export function confirmModbusMap(
  projectId: string,
  proposals: ModbusMapProposal[],
  decisions: ModbusMapDecision[],
  authorization?: OnboardingAuthorization,
): Promise<WriteResult> {
  return request<WriteResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/modbus-map/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({ proposals, decisions, authorization: authorization ?? null }),
    },
  );
}

// ── Unbekannte Superblöcke (Massenimport, Mehrfach-Durchlauf, Issue #271) ────────────────
// Welche vom gespeicherten Projekt-Graphen referenzierten Superblöcke der Bibliothek fehlen.
export function getProjectMissingSuperblocks(projectId: string): Promise<SuperblockGapReport> {
  return request<SuperblockGapReport>(
    `${API}/projects/${encodeURIComponent(projectId)}/superblocks/missing`,
  );
}

// Dieselbe Prüfung für einen mitgegebenen Graphen (Bundle-Import vor dem Speichern).
export function getMissingSuperblocks(graph: SignalGraph): Promise<SuperblockGapReport> {
  return request<SuperblockGapReport>(`${API}/superblocks/missing`, {
    method: 'POST',
    body: JSON.stringify({ graph }),
  });
}

// ── Projekte ─────────────────────────────────────────────────────────────────────────

export function listProjects(): Promise<ProjectList> {
  return request<ProjectList>(`${API}/projects`);
}

export function createProject(payload: ProjectCreate): Promise<ProjectMeta> {
  return request<ProjectMeta>(`${API}/projects`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getProject(projectId: string): Promise<ProjectMeta> {
  return request<ProjectMeta>(`${API}/projects/${encodeURIComponent(projectId)}`);
}

// Projekt-Einstellungen setzen (Issue #118): OPC-Endpoint und/oder Standard-Zykluszeit. Partielles
// Update — nur die mitgegebenen Felder wirken (opcEndpoint === null entfernt den Endpoint bewusst).
// Braucht das Recht „editieren"; das Backend quittiert defaultCycleMs < 1 mit 422. Antwort ist die
// aktualisierte Projekt-Meta (mit dem persistierten opcEndpoint/defaultCycleMs).
export function updateProjectSettings(
  projectId: string,
  settings: ProjectSettingsUpdate,
): Promise<ProjectMeta> {
  return request<ProjectMeta>(`${API}/projects/${encodeURIComponent(projectId)}/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// Das Vier-Augen-Prinzip eines Projekts ein-/ausschalten (Issue #114). Nur Admin — das Backend
// erzwingt das Recht serverseitig (403); die Antwort trägt den neuen Zustand. Die Einstellungen-
// Ansicht spiegelt danach diesen Zustand.
export function setProjectFourEyes(
  projectId: string,
  enabled: boolean,
): Promise<ProjectFourEyesResult> {
  return request<ProjectFourEyesResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/four-eyes`,
    { method: 'PUT', body: JSON.stringify({ enabled }) },
  );
}

// ── Historie / Rollback / Audit (Issue #82) ──────────────────────────────────────────────

// Eine Seite der Projekt-Historie (neueste zuerst). `before` (voller Hash) und `limit` steuern
// die Paginierung; für die nächste (ältere) Seite den zurückgegebenen `nextBefore` als `before`
// wieder mitgeben.
export function getHistory(
  projectId: string,
  opts: { limit?: number; before?: string } = {},
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.before) params.set('before', opts.before);
  const q = params.toString();
  return request<HistoryResponse>(
    `${API}/projects/${encodeURIComponent(projectId)}/history${q ? `?${q}` : ''}`,
  );
}

// Lazy-Diff einer einzelnen Objektdatei (catalog/graph/schematic/data-sources/meta) zu einer
// Revision — erst beim Aufklappen eines Commits geladen, damit die Zeitleiste schlank bleibt.
export function getHistoryDiff(
  projectId: string,
  rev: string,
  object: string,
): Promise<HistoryDiff> {
  return request<HistoryDiff>(
    `${API}/projects/${encodeURIComponent(projectId)}/history/${encodeURIComponent(
      rev,
    )}/diff?object=${encodeURIComponent(object)}`,
  );
}

// Nicht-destruktiver Rollback: stellt den Baum von `targetRev` als NEUEN Commit her (die
// Vorwärts-Historie bleibt vollständig). Braucht das Recht „editieren" — sonst 403 vom Backend.
export function revertProject(
  projectId: string,
  targetRev: string,
  note?: string,
): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/revert`, {
    method: 'POST',
    body: JSON.stringify(note != null ? { targetRev, note } : { targetRev }),
  });
}

// Den Audit-Trail eines Projekts lesen (die verkettete Ereignisliste + das Verifikationsergebnis
// der Hash-Kette). Die Einträge sind frei geformt; die Audit-Ansicht zeigt sie roh und exportiert
// sie als .jsonl.
export function getAudit(projectId: string): Promise<AuditResponse> {
  return request<AuditResponse>(`${API}/projects/${encodeURIComponent(projectId)}/audit`);
}

// ── Signalkatalog ────────────────────────────────────────────────────────────────────

export function getCatalog(projectId: string): Promise<SignalCatalog> {
  return request<SignalCatalog>(`${API}/projects/${encodeURIComponent(projectId)}/catalog`);
}

export function putCatalog(projectId: string, catalog: SignalCatalog): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/catalog`, {
    method: 'PUT',
    body: JSON.stringify(catalog),
  });
}

export function validateCatalog(
  projectId: string,
  catalog: SignalCatalog,
): Promise<ValidateResult> {
  return request<ValidateResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/catalog/validate`,
    { method: 'POST', body: JSON.stringify(catalog) },
  );
}

// ── UA-Browse-Onboarding (#152) ──────────────────────────────────────────────────────────
// Ein autorisiertes, read-only OPC-UA-Browse schlägt Tag-Kandidaten (mit OPC-Bindung) vor; der
// Betreiber kuratiert + bestätigt, bevor der Entwurf zum Signalkatalog wird. Der Draft speichert
// NICHTS und schreibt NICHTS an die Anlage — erst das Confirm schreibt den bestätigten Katalog.

export function uaBrowseDraft(
  projectId: string,
  endpoint: string,
): Promise<UaBrowseDraftResult> {
  return request<UaBrowseDraftResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/onboarding/ua-browse/draft`,
    { method: 'POST', body: JSON.stringify({ endpoint }) },
  );
}

export function uaBrowseConfirm(
  projectId: string,
  catalog: SignalCatalog,
  decisions: UaBrowseDecision[],
  authorization?: UaBrowseAuthorization,
): Promise<WriteResult> {
  return request<WriteResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/onboarding/ua-browse/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({ catalog, decisions, authorization: authorization ?? null }),
    },
  );
}

// ── Signalgraph ──────────────────────────────────────────────────────────────────────

export function getGraph(projectId: string): Promise<SignalGraph> {
  return request<SignalGraph>(`${API}/projects/${encodeURIComponent(projectId)}/graph`);
}

export function putGraph(projectId: string, graph: SignalGraph): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/graph`, {
    method: 'PUT',
    body: JSON.stringify(graph),
  });
}

export function validateGraph(projectId: string, graph: SignalGraph): Promise<ValidateResult> {
  return request<ValidateResult>(
    `${API}/projects/${encodeURIComponent(projectId)}/graph/validate`,
    { method: 'POST', body: JSON.stringify(graph) },
  );
}

// Default-Regelmuster eines platzierten Symbols stempeln (Issue #21). Liefert den idempotent in den
// aktuellen Graphen gemergten Vorschlag zurück (das Backend speichert NICHT — das Einsetzen ist eine
// bewusste Handlung). Regelschleifen stehen im Vorschlag per Default in Hand.
export function stampGraph(projectId: string, payload: StampRequest): Promise<StampResult> {
  return request<StampResult>(`${API}/projects/${encodeURIComponent(projectId)}/graph/stamp`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── BORIS-Datei-Import (Issue #27) ──────────────────────────────────────────────────────
// Lädt eine BORIS-Systemdatei (.BSY) samt optionaler Superblöcke (.SBL) hoch und importiert sie in
// das genannte Projekt: die Dateien landen in der NICHT git-versionierten Datei-Ablage, das
// abgeleitete graph.json + der Katalog werden ins Projekt geschrieben (git-Commit) und das Projekt
// als BORIS-Import markiert. Das Frontend entscheidet über die Projektwahl (neues vs. aktuelles),
// indem es diesen Aufruf auf dem gewünschten Projekt tätigt. Kein Content-Type setzen — der Browser
// setzt die multipart-Grenze selbst.
export async function importBoris(
  projectId: string,
  bsy: File,
  sbl: File[] = [],
): Promise<BorisImportResult> {
  const form = new FormData();
  form.append('bsy', bsy);
  for (const file of sbl) form.append('sbl', file);
  let res: Response;
  try {
    res = await fetch(`${API}/projects/${encodeURIComponent(projectId)}/import/boris`, {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause));
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as BorisImportResult;
}

// ── Anlagenschema (visuelle P&ID-Darstellung) ──────────────────────────────────────────

// Das Anlagenschema lesen (platzierte Symbole + Positionen + Verrohrung). Aus ihm baut die
// Anlagen-Ansicht die Canvas beim Projektwechsel wieder auf.
export function getSchematic(projectId: string): Promise<PlantSchematic> {
  return request<PlantSchematic>(`${API}/projects/${encodeURIComponent(projectId)}/schematic`);
}

// Das Anlagenschema schreiben (Backend validiert gegen schematic.schema.json + git-Commit).
export function putSchematic(
  projectId: string,
  schematic: PlantSchematic,
): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/schematic`, {
    method: 'PUT',
    body: JSON.stringify(schematic),
  });
}

// ── Datenablage (CSV) + Datenquellen-Zuordnung ──────────────────────────────────────────
// Die CSV-Dateien liegen projektspezifisch im NICHT git-versionierten Datenverzeichnis der
// Instanz; NUR die Zuordnung (data-sources.json) ist git-versioniert. Die Daten-Ansicht lädt CSVs
// hoch, liest ihre Spalten und speichert die grafische Zuordnung per PUT.

// Eine CSV in das Projekt-Datenverzeichnis hochladen (multipart). Kein Content-Type setzen —
// der Browser setzt die multipart-Grenze selbst; deshalb NICHT über request() (das würde JSON annehmen).
export async function uploadDataFile(projectId: string, file: File): Promise<DataFileInfo> {
  const form = new FormData();
  form.append('file', file);
  let res: Response;
  try {
    res = await fetch(`${API}/projects/${encodeURIComponent(projectId)}/files`, {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', cause instanceof Error ? cause.message : String(cause));
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as DataFileInfo;
}

export function listDataFiles(projectId: string): Promise<DataFileList> {
  return request<DataFileList>(`${API}/projects/${encodeURIComponent(projectId)}/files`);
}

export function getFileColumns(projectId: string, name: string): Promise<DataFileColumns> {
  return request<DataFileColumns>(
    `${API}/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(name)}/columns`,
  );
}

export function deleteDataFile(projectId: string, name: string): Promise<void> {
  return request<void>(
    `${API}/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(name)}`,
    { method: 'DELETE' },
  );
}

export function getDataSources(projectId: string): Promise<DataSources> {
  return request<DataSources>(`${API}/projects/${encodeURIComponent(projectId)}/data-sources`);
}

export function putDataSources(
  projectId: string,
  dataSources: DataSources,
): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/data-sources`, {
    method: 'PUT',
    body: JSON.stringify(dataSources),
  });
}

// ── Monitoring-Konfiguration (monitoring.json, Issue #81/#135) ──────────────────────────
// Die git-versionierte Monitoring-Konfiguration lesen/schreiben. Das Überwachungs-Dashboard liest
// daraus die pro-Tag gewählte Anzeigeart (displayTypes) und schreibt Änderungen daran zurück —
// die übrigen Felder (Dashboards, Alarm-Politik, Übersteuerungen) bleiben erhalten. Der PUT braucht
// das Recht „editieren" (sonst 403 vom Backend); GET genügt „lesen".

export function getMonitoring(projectId: string): Promise<MonitoringConfig> {
  return request<MonitoringConfig>(`${API}/projects/${encodeURIComponent(projectId)}/monitoring`);
}

export function putMonitoring(
  projectId: string,
  cfg: MonitoringConfig,
): Promise<WriteResult> {
  return request<WriteResult>(`${API}/projects/${encodeURIComponent(projectId)}/monitoring`, {
    method: 'PUT',
    body: JSON.stringify(cfg),
  });
}

// ── Modelle / Registry ─────────────────────────────────────────────────────────────────

// Modelle der Registry auflisten (je Modell die Versionen, ihr Status und die aktive Version).
// Speist das Modell-Dropdown des model-inference-Knotens in der Signalgraph-Ansicht und die
// Registry-Ansicht der Trainings-Seite. Über request() (Bearer-Token wird angehängt).
export function listModels(): Promise<ModelList> {
  return request<ModelList>(`${API}/models`);
}

// Das Manifest einer Modellversion lesen (Ports, Metriken, Notebook-Hash, Governance). Ein
// nicht vorhandenes Artefakt quittiert das Backend mit 404 (not_found).
export function getModelManifest(name: string, version: string): Promise<ModelManifest> {
  return request<ModelManifest>(
    `${API}/models/${encodeURIComponent(name)}/${encodeURIComponent(version)}`,
  );
}

// Eine Modellversion freigeben (Status → 'approved'). Braucht das Recht „deployen"; approvedBy ist
// serverseitig an die Identität gebunden (ein Body-Wert wird ignoriert). Liefert das aktualisierte
// Manifest. Ungültiger Übergang → 409, fehlendes Artefakt → 404.
export function approveModel(name: string, version: string, note?: string): Promise<ModelManifest> {
  return request<ModelManifest>(
    `${API}/models/${encodeURIComponent(name)}/${encodeURIComponent(version)}/approve`,
    { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
  );
}

// Den aktiven Pointer der Registry auf eine Modellversion setzen (nur auf 'approved'-Versionen
// erlaubt). Braucht das Recht „deployen". Liefert das Manifest der nun aktiven Version.
export function activateModel(name: string, version: string): Promise<ModelManifest> {
  return request<ModelManifest>(
    `${API}/models/${encodeURIComponent(name)}/${encodeURIComponent(version)}/activate`,
    { method: 'POST' },
  );
}

// ── Runs ─────────────────────────────────────────────────────────────────────────────

// Läufe eines Projekts abfragen — samt Zeiger auf den aktiven (noch laufenden) Lauf. Die HMI liest
// das beim Betreten von Monitor/Dashboard, um einen bereits laufenden Lauf (z. B. Live/Deploy) zu
// erkennen und lesend zu abonnieren, statt einen zweiten zu starten.
export function listRuns(projectId: string): Promise<RunList> {
  return request<RunList>(`${API}/projects/${encodeURIComponent(projectId)}/runs`);
}

export function startRun(projectId: string, payload?: RunCreate): Promise<RunInfo> {
  return request<RunInfo>(`${API}/projects/${encodeURIComponent(projectId)}/runs`, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });
}

export function stopRun(projectId: string, runId: string): Promise<void> {
  return request<void>(
    `${API}/projects/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}`,
    { method: 'DELETE' },
  );
}

// Ablaufgeschwindigkeit eines laufenden Laufs live verstellen (Zykluszeit und/oder Zeitraffer).
export function updatePace(
  projectId: string,
  runId: string,
  pace: PaceRequest,
): Promise<RunInfo> {
  return request<RunInfo>(
    `${API}/projects/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}/pace`,
    { method: 'POST', body: JSON.stringify(pace) },
  );
}

// ── Deploys (Live-Freigabe, SAFE-40/41) ────────────────────────────────────────────────
// Der Live-Weg der HMI kapselt propose → approve → run(mode=live). Ist das Vier-Augen-Prinzip des
// Projekts aus (Default), darf dieselbe authentisierte Person vorschlagen UND freigeben, sodass ein
// Live-Lauf per Klick möglich ist. proposedBy/approvedBy binden ans Session-Recht (Body nur Notiz).

// Stufe 1: einen Deploy vorschlagen — friert den aktuellen Graph/Katalog ein. Braucht „veröffentlichen".
export function proposeDeploy(projectId: string, payload: DeployCreate = {}): Promise<DeployInfo> {
  return request<DeployInfo>(`${API}/projects/${encodeURIComponent(projectId)}/deploys`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Stufe 2: einen Deploy freigeben. Braucht „deployen"; bei aktivem Vier-Augen scheitert die
// Selbst-Freigabe (409 self_approval).
export function approveDeploy(deployId: string, payload: DeployApprove = {}): Promise<DeployInfo> {
  return request<DeployInfo>(`${API}/deploys/${encodeURIComponent(deployId)}/approve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── WebSocket-Monitor ─────────────────────────────────────────────────────────────────

export interface MonitorHandlers {
  onMessage: (msg: MonitorMessage) => void;
  onOpen?: () => void;
  onClose?: (ev: CloseEvent) => void;
  onError?: (ev: Event) => void;
}

// Ein schlanker WebSocket-Griff: absolute wss/ws-URL aus dem aktuellen Origin bilden,
// optional einen Lauf abonnieren und einen sauberen close() zurückgeben.
export interface MonitorConnection {
  close: () => void;
}

export function connectMonitor(
  projectId: string,
  runId: string | undefined,
  handlers: MonitorHandlers,
): MonitorConnection {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // WebSockets können keine Header setzen → Token als Query-Parameter (das Backend akzeptiert
  // `token` als Fallback zum Bearer-Header).
  const token = getToken();
  const auth = token ? `?token=${encodeURIComponent(token)}` : '';
  const url = `${scheme}://${window.location.host}${WS}/projects/${encodeURIComponent(
    projectId,
  )}/monitor${auth}`;
  const ws = new WebSocket(url);

  ws.onopen = () => {
    // Optionales Abonnement: mit runId hängt sich der Monitor an genau diesen Lauf, sonst an
    // den jüngsten Lauf des Projekts (Backend-Verhalten).
    if (runId) ws.send(JSON.stringify({ op: 'subscribe', runId }));
    handlers.onOpen?.();
  };
  ws.onmessage = (ev) => {
    try {
      handlers.onMessage(JSON.parse(ev.data as string) as MonitorMessage);
    } catch {
      /* ungültige Nachricht ignorieren — der Kanal ist ein passiver Mitleser */
    }
  };
  ws.onclose = (ev) => handlers.onClose?.(ev);
  ws.onerror = (ev) => handlers.onError?.(ev);

  return {
    close: () => {
      // Handler abhängen, damit ein bewusster close() keinen Reconnect-Hinweis auslöst.
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        /* bereits geschlossen */
      }
    },
  };
}
